/**
 * « Vos coordonnées » — le premier écran d'un responsable qui vient de créer
 * son compte.
 *
 * ## Pourquoi le responsable d'abord
 *
 * Ces informations étaient demandées au pire moment : sur la page des
 * stages, quand le parent avait déjà rempli la fiche de son enfant et pensait
 * avoir fini (« Vos coordonnées — 6 informations à compléter »). Et la fiche
 * enfant, elle, lui faisait retaper la même adresse et proposait « Vous-même ·
 * téléphone à compléter » dans les personnes autorisées.
 *
 * Demandées ici, une fois et au début, elles servent ensuite partout : l'adresse
 * est reprise dans la fiche de chaque enfant, le parent devient une personne
 * autorisée en un clic, et la page des stages n'a plus rien à réclamer.
 *
 * ## La première question : qui inscrit ?
 *
 * Un parent, un proche (grand-parent, famille d'accueil…) ou une organisation
 * (école, CPAS, maison d'accueil). La première version de cet écran ne la
 * posait pas : tout nouveau compte devenait « parent », et plus rien dans le
 * parcours ne repassait par `/profile`, où le choix existait. Or le type
 * décide du tiers en comptabilité et du nom porté par les factures. Il se
 * demande donc avant tout le reste.
 *
 * ## Ce que l'écran ne demande pas
 *
 * Le second téléphone, la newsletter : ils restent sur `/profile`. Le
 * registre national, lui, est ici — facultatif, mais demandé au parent dès
 * le début, parce que c'est la seule chose qui conditionne l'attestation
 * fiscale et qu'on ne repassera plus par là.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useKidStore } from '../../stores/kidStore';
import { Bloc, Choix, MentionObligatoire, Saisie } from '../../components/forms/kid/champs';
import RegistreNationalParent from '../../components/profile/RegistreNationalParent';
import {
  COORDONNEES_VIDES,
  TYPES_DE_COMPTE,
  erreursCoordonnees,
  estOrganisation,
  profilDepuisCoordonnees,
  type Coordonnees,
  type Erreurs,
  type TypeDeCompte,
} from '../../lib/coordonnees';

/** Une destination interne seulement : `?suite=` ne doit pas mener hors du site. */
const destinationSure = (suite: string | null) =>
  suite && suite.startsWith('/') && !suite.startsWith('//') ? suite : null;

/** Les valeurs que d'anciens imports ont laissées à la place d'un numéro absent. */
const nrnPropre = (v: string | null | undefined) =>
  ['EMPTY', '000', 'NULL'].includes((v ?? '').trim().toUpperCase()) ? '' : (v ?? '');

const CoordonneesPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { profile, profileLoaded, fetchProfile, updateProfile, user } = useAuthStore();
  const { kids, fetchKids } = useKidStore();

  const [valeur, setValeur] = useState<Coordonnees>(COORDONNEES_VIDES);
  const [erreurs, setErreurs] = useState<Erreurs>({});
  const [enregistrement, setEnregistrement] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);

  useEffect(() => {
    if (user && !profileLoaded) fetchProfile();
    if (user) fetchKids();
  }, [user, profileLoaded, fetchProfile, fetchKids]);

  // Ce qui est déjà connu est pré-rempli : souvent le nom, venu de Google
  useEffect(() => {
    if (!profile) return;
    setValeur((v) => ({
      ...v,
      account_type: (profile.account_type as TypeDeCompte) || 'parent',
      organisation_name: v.organisation_name || profile.organisation_name || '',
      company_number: v.company_number || profile.company_number || '',
      prenom: v.prenom || profile.prenom || '',
      nom: v.nom || profile.nom || '',
      adresse: v.adresse || profile.adresse || '',
      cpostal: v.cpostal || profile.cpostal || '',
      localite: v.localite || profile.localite || '',
      telephone: v.telephone || profile.telephone || '',
      nnational: v.nnational || nrnPropre(profile.nnational),
    }));
  }, [profile]);

  const organisation = estOrganisation(valeur.account_type);
  const parent = valeur.account_type === 'parent';
  /* Le parcours en trois temps ne se montre qu'à un nouveau compte. */
  const premiereFois = kids.length === 0;
  const nrnDesEnfants = useMemo(() => kids.map((k) => k.n_national ?? '').filter(Boolean), [kids]);

  const maj = (champs: Partial<Coordonnees>) => setValeur((v) => ({ ...v, ...champs }));

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    const trouvees = erreursCoordonnees(valeur, { nrnDesEnfants });
    setErreurs(trouvees);
    if (Object.keys(trouvees).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setEnregistrement(true);
    setEchec(null);
    try {
      await updateProfile(profilDepuisCoordonnees(valeur));
      navigate(destinationSure(params.get('suite')) ?? (premiereFois ? '/kids/new' : '/activities'), {
        replace: true,
      });
    } catch {
      setEchec('Vos coordonnées n’ont pas pu être enregistrées. Réessayez dans un instant.');
    } finally {
      setEnregistrement(false);
    }
  };

  if (!profileLoaded) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-8 md:py-12">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
          {premiereFois ? 'Bienvenue ! Commençons par vous' : 'Vos coordonnées'}
        </h1>
        <p className="mt-1 text-gray-600">
          Une seule fois&nbsp;: nous les reprendrons dans la fiche de vos enfants et pour toutes
          vos inscriptions.
        </p>
      </div>

      {premiereFois && (
        <ol className="mb-6 flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label="Les étapes de l’inscription">
          <li className="font-semibold text-primary-700">1. Vos coordonnées</li>
          <li className="text-gray-500">2. {organisation ? 'L’enfant' : 'Votre enfant'}</li>
          <li className="text-gray-500">3. Ses stages</li>
        </ol>
      )}

      <form
        onSubmit={enregistrer}
        noValidate
        className="space-y-8 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-8"
      >
        <MentionObligatoire />

        {echec && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{echec}</p>}

        <Bloc titre="Je suis…" requis>
          <Choix<TypeDeCompte>
            valeur={valeur.account_type}
            onChange={(account_type) => maj({ account_type })}
            options={TYPES_DE_COMPTE}
          />
        </Bloc>

        {organisation && (
          <Bloc
            titre="Votre organisation"
            intro="Son nom figure sur les factures. Le numéro d’entreprise n’est pas obligatoire."
          >
            <Saisie
              label="Nom de l’organisation"
              requis
              valeur={valeur.organisation_name}
              onChange={(v) => maj({ organisation_name: v })}
              erreur={erreurs.organisation_name}
              autoComplete="organization"
              placeholder="CPAS de Forest, École du Bois…"
            />
            <Saisie
              label="Numéro d’entreprise (BCE)"
              facultatif
              valeur={valeur.company_number}
              onChange={(v) => maj({ company_number: v })}
              erreur={erreurs.company_number}
              placeholder="0123.456.789"
              inputMode="numeric"
            />
          </Bloc>
        )}

        <Bloc
          titre={organisation ? 'La personne de contact' : 'Qui êtes-vous ?'}
          intro={
            organisation
              ? 'La personne que l’équipe appelle pour tout ce qui concerne les inscriptions et les enfants.'
              : undefined
          }
        >
          <div className="grid gap-5 md:grid-cols-2">
            <Saisie
              label="Prénom"
              requis
              valeur={valeur.prenom}
              onChange={(v) => maj({ prenom: v })}
              erreur={erreurs.prenom}
              autoComplete="given-name"
            />
            <Saisie
              label="Nom"
              requis
              valeur={valeur.nom}
              onChange={(v) => maj({ nom: v })}
              erreur={erreurs.nom}
              autoComplete="family-name"
            />
          </div>
          <Saisie
            label="Téléphone"
            requis
            type="tel"
            valeur={valeur.telephone}
            onChange={(v) => maj({ telephone: v })}
            erreur={erreurs.telephone}
            autoComplete="tel"
            placeholder="0470 12 34 56"
            aide={
              organisation
                ? 'Le numéro où l’équipe joint la personne de contact pendant le stage.'
                : 'Le numéro où l’équipe vous joint pendant le stage.'
            }
          />
        </Bloc>

        <Bloc
          titre={organisation ? 'L’adresse de l’organisation' : 'Votre adresse'}
          intro={
            organisation
              ? 'Celle qui figure sur les factures. L’adresse de chaque enfant se renseigne dans sa fiche.'
              : 'Nous la proposerons pour vos enfants ; vous pourrez en indiquer une autre pour chacun.'
          }
        >
          <Saisie
            label="Adresse"
            requis
            valeur={valeur.adresse}
            onChange={(v) => maj({ adresse: v })}
            erreur={erreurs.adresse}
            autoComplete="street-address"
            placeholder="Rue et numéro"
          />
          <div className="grid gap-5 md:grid-cols-2">
            <Saisie
              label="Code postal"
              requis
              valeur={valeur.cpostal}
              onChange={(v) => maj({ cpostal: v.replace(/\D/g, '').slice(0, 4) })}
              erreur={erreurs.cpostal}
              autoComplete="postal-code"
              placeholder="1030"
            />
            <Saisie
              label="Localité"
              requis
              valeur={valeur.localite}
              onChange={(v) => maj({ localite: v })}
              erreur={erreurs.localite}
              autoComplete="address-level2"
              placeholder="Schaerbeek"
            />
          </div>
        </Bloc>

        {parent && (
          <RegistreNationalParent
            dateNaissance={valeur.date_naissance}
            onDateNaissance={(v) => maj({ date_naissance: v })}
            nnational={valeur.nnational}
            onNnational={(v) => maj({ nnational: v })}
            erreurs={erreurs}
          />
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={enregistrement} className="btn-primary inline-flex items-center">
            {enregistrement && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {premiereFois
              ? organisation
                ? 'Continuer vers l’enfant'
                : 'Continuer vers mon enfant'
              : 'Enregistrer et continuer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CoordonneesPage;
